/**
 * childProfiles.js — Static child neighbor profiles.
 *
 * These three kid records are appended to the main `neighbors` array
 * (see mockData.js) so they are clickable through the same
 * `/app/neighbors/:id` route the CM already uses for adults. Every
 * field originates from a mocked PowerSchool SFTP sync — the
 * NeighborProfile page detects `isChild: true` and renders the
 * education-first layout with a PowerSchool ribbon at the top.
 *
 * These records are intentionally kept in a SEPARATE file (not the
 * demo-resettable mockData block) so they persist across demo resets.
 * They are anchored to existing household narratives:
 *   - Kayla Rodriguez  — Maria Rodriguez's daughter, attendance risk
 *   - Jayden Thompson  — James Thompson's son, discipline risk
 *   - Emma Chen-Rivera — IEP flagged, on-track (Carlos Rivera stepchild)
 */

const nowIso = new Date().toISOString();

export const childProfiles = [
  {
    id: 'nbr_k1',
    isChild: true,
    firstName: 'Kayla',
    lastName: 'Rodriguez',
    age: 11,
    dob: '2014-04-22',
    gender: 'Female',
    householdId: 'hh_1',
    guardianId: 'nbr_1',
    guardianName: 'Maria Rodriguez',
    address: '1246 English Ave',
    city: 'Indianapolis',
    state: 'IN',
    zip: '46201',
    riskLevel: 'Dependent',
    riskScore: 2.1,
    flags: ['Attendance Watch', 'Dependent', 'Minor'],
    lastReview: null,
    nextReview: '2026-02-14',
    lastContact: '2025-01-20',
    monthlyIncome: 0,
    fplPct: 78,
    householdSize: 4,
    housingStatus: 'Rent',
    educationLevel: 'Some HS',
    employmentStatus: 'N/A',
    enrolledPrograms: ['prog_8', 'prog_9'],
    assignedStaff: 'staff_1',
    assignedTeam: 'Team A',
    email: '',
    phone: '',
    school: {
      name: 'Arsenal Technical High School',
      grade: '6th grade',
      counselor: 'Ms. Rebecca Alvarez',
      lastSync: nowIso,
    },
    powerSchool: {
      gpa: { current: 2.4, previous: 2.8, trend: 'down' },
      attendance: { percent: 84, absences: 16, tardies: 8, unexcused: 11 },
      discipline: { referrals: 1, suspensionDays: 0, lastIncident: '2025-11-03 · Class disruption' },
      standardizedTests: [
        { subject: 'ELA',  percentile: 42, band: 'Approaching' },
        { subject: 'Math', percentile: 38, band: 'Approaching' },
      ],
      iep: { active: false, flag: null, plan: null },
      watchlist: { tier: 'high', reason: 'Attendance below 90% + GPA drop 0.4 pts' },
    },
    caseNotes: [],
  },
  {
    id: 'nbr_k2',
    isChild: true,
    firstName: 'Jayden',
    lastName: 'Thompson',
    age: 14,
    dob: '2011-08-09',
    gender: 'Male',
    householdId: 'hh_2',
    guardianId: 'nbr_2',
    guardianName: 'James Thompson',
    address: '2114 East 10th St',
    city: 'Indianapolis',
    state: 'IN',
    zip: '46201',
    riskLevel: 'Dependent',
    riskScore: 1.6,
    flags: ['Discipline Watch', 'Academic Watch', 'Dependent', 'Minor'],
    lastReview: null,
    nextReview: '2026-02-07',
    lastContact: '2025-01-18',
    monthlyIncome: 0,
    fplPct: 95,
    householdSize: 2,
    housingStatus: 'Rent',
    educationLevel: 'Some HS',
    employmentStatus: 'N/A',
    enrolledPrograms: ['prog_8'],
    assignedStaff: 'staff_2',
    assignedTeam: 'Team B',
    email: '',
    phone: '',
    school: {
      name: 'Arsenal Technical High School',
      grade: '9th grade',
      counselor: 'Mr. Devon Price',
      lastSync: nowIso,
    },
    powerSchool: {
      gpa: { current: 1.8, previous: 2.3, trend: 'down' },
      attendance: { percent: 91, absences: 8, tardies: 12, unexcused: 6 },
      discipline: { referrals: 4, suspensionDays: 1, lastIncident: '2025-12-02 · Verbal altercation \u00b7 1-day OSS' },
      standardizedTests: [
        { subject: 'ELA',  percentile: 28, band: 'Below' },
        { subject: 'Math', percentile: 31, band: 'Below' },
      ],
      iep: { active: false, flag: null, plan: null },
      watchlist: { tier: 'critical', reason: '4 discipline referrals + GPA below 2.0' },
    },
    caseNotes: [],
  },
  {
    id: 'nbr_k3',
    isChild: true,
    firstName: 'Emma',
    lastName: 'Chen-Rivera',
    age: 8,
    dob: '2017-06-11',
    gender: 'Female',
    householdId: 'hh_16',
    guardianId: 'nbr_10',
    guardianName: 'Carlos Rivera',
    address: '3402 Brookville Rd',
    city: 'Indianapolis',
    state: 'IN',
    zip: '46201',
    riskLevel: 'Dependent',
    riskScore: 3.2,
    flags: ['IEP Monitoring', 'Dependent', 'Minor'],
    lastReview: null,
    nextReview: '2026-03-15',
    lastContact: '2025-01-15',
    monthlyIncome: 0,
    fplPct: 40,
    householdSize: 1,
    housingStatus: 'Homeless',
    educationLevel: 'None',
    employmentStatus: 'N/A',
    enrolledPrograms: ['prog_8', 'prog_9'],
    assignedStaff: 'staff_1',
    assignedTeam: 'Team A',
    email: '',
    phone: '',
    school: {
      name: 'IPS 14 \u2014 James Whitcomb Riley',
      grade: '3rd grade',
      counselor: 'Ms. Priya Nair',
      lastSync: nowIso,
    },
    powerSchool: {
      gpa: { current: 3.1, previous: 3.1, trend: 'flat' },
      attendance: { percent: 96, absences: 4, tardies: 2, unexcused: 0 },
      discipline: { referrals: 0, suspensionDays: 0, lastIncident: null },
      standardizedTests: [
        { subject: 'ELA',  percentile: 55, band: 'Meets' },
        { subject: 'Math', percentile: 61, band: 'Meets' },
      ],
      iep: {
        active: true,
        flag: 'ADHD (per Section 504)',
        plan: 'Extended time on tests \u00b7 preferential seating \u00b7 fidget tool permitted',
      },
      watchlist: { tier: 'low', reason: 'IEP quarterly review \u2014 on track' },
    },
    caseNotes: [],
  },
];

export function isChildProfile(id) {
  return childProfiles.some(c => c.id === id);
}

export function getChildProfile(id) {
  return childProfiles.find(c => c.id === id) || null;
}
